// src/ui/screens/affiliation/AffiliationScreen.jsx
//
// Web port of lib/ui/screens/affiliation/affiliation_screen.dart

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import {
  affiliationService,
  SellerCategoryType,
} from '../../../services/affiliation/affiliationService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── Small icon helpers (mirrors Icons.apartment_rounded / person_outline_rounded) ──

const BuildingIcon = ({ size = 18, color = COLORS.orange }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M6 21V7a1 1 0 011-1h4a1 1 0 011 1v14M15 21V4a1 1 0 011-1h3a1 1 0 011 1v17M9 9h.01M9 13h.01M9 17h.01" />
  </svg>
);

const PersonIcon = ({ size = 18, color = COLORS.orange }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CategoryIcon = ({ type, size, color }) =>
  type === SellerCategoryType.business ? (
    <BuildingIcon size={size} color={color} />
  ) : (
    <PersonIcon size={size} color={color} />
  );

// ── Toast (lightweight, self-contained; swap for your app's toast system if preferred) ──

const useToast = () => {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, isError = false) => {
    setToast({ message, isError });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  }, []);

  const ToastEl = toast ? (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 px-4 py-2.5 rounded-xl text-sm font-medium text-black shadow-lg"
      style={{ backgroundColor: toast.isError ? '#EF4444' : COLORS.orange }}
    >
      {toast.message}
    </div>
  ) : null;

  return [ToastEl, showToast];
};

// ── Section header ───────────────────────────────────────────────────────────

const SectionHeader = ({ title, count }) => (
  <div className="flex items-center gap-2">
    <span className="text-white text-[15px] font-bold">{title}</span>
    {count > 0 && (
      <span
        className="px-2 py-0.5 rounded-full text-[11px] font-bold"
        style={{ backgroundColor: `${COLORS.orange}26`, color: COLORS.orange }}
      >
        {count}
      </span>
    )}
  </div>
);

const EmptyHint = ({ text }) => (
  <p className="text-white/20 text-[12.5px] py-1.5">{text}</p>
);

// ── Affiliation Screen ────────────────────────────────────────────────────────

export const AffiliationScreen = () => {
  const navigate = useNavigate();

  const [loadingMyType, setLoadingMyType] = useState(true);
  const [myCategoryType, setMyCategoryType] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const [incomingRequests, setIncomingRequests] = useState([]);
  const [proposed, setProposed] = useState([]);
  const [affiliates, setAffiliates] = useState([]);

  const [ToastEl, showToast] = useToast();

  const isPersonal = myCategoryType === SellerCategoryType.personal;
  const targetType = isPersonal ? SellerCategoryType.business : SellerCategoryType.personal;
  const pageTitle = isPersonal ? 'Affiliated Companies' : 'Affiliated Profiles';
  const affiliatesLabel = pageTitle;
  const searchHint = isPersonal ? 'Search verified companies' : 'Search personal accounts';
  const pageSubtitle = isPersonal
    ? 'Companies affiliated with your personal account'
    : 'Personal accounts affiliated with your business';

  // ── Load my category type ──────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setLoadingMyType(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const verification = snap.data()?.verification;
        setMyCategoryType(verification?.categoryType ?? null);
      } catch (error) {
        console.error('Load category type error:', error);
      } finally {
        setLoadingMyType(false);
      }
    };
    load();
  }, []);

  // ── Live subscriptions ──────────────────────────────────────────────────

  useEffect(() => {
    const unsub = affiliationService.streamIncomingRequests(setIncomingRequests);
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const unsub = affiliationService.streamProposedByMe(setProposed);
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const unsub = affiliationService.streamAffiliates(setAffiliates);
    return () => unsub?.();
  }, []);

  // ── Search (debounced) ────────────────────────────────────────────────

  useEffect(() => {
    window.clearTimeout(debounceRef.current);

    const trimmed = searchText.trim();
    if (!trimmed) {
      setSuggestions([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      const results = await affiliationService.searchAccounts({
        query: trimmed,
        targetType,
      });
      setSuggestions(results);
      setSearching(false);
    }, 400);

    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, targetType]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const proposeAffiliate = async (user) => {
    const success = await affiliationService.sendAffiliationRequest({ targetUser: user });
    setSearchText('');
    setSuggestions([]);
    showToast(
      success
        ? `Affiliation request sent to @${user.username || ''}`
        : 'Already affiliated or a request is already pending',
      !success
    );
  };

  const withdraw = async (affiliationId) => {
    await affiliationService.withdrawAffiliationRequest(affiliationId);
    showToast('Request withdrawn');
  };

  const respond = async (affiliationId, approve) => {
    await affiliationService.respondToAffiliationRequest({ affiliationId, approve });
    showToast(approve ? 'Affiliation approved' : 'Affiliation declined');
  };

  const myUid = auth.currentUser?.uid;

  // ── Shared search bar (used by both layouts) ──────────────────────────────

  const SearchBar = () => (
    <div className="relative">
      <div
        className="flex items-center gap-2 px-3.5 rounded-2xl"
        style={{ backgroundColor: COLORS.card }}
      >
        <svg className="w-5 h-5 text-white/40 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
        <span className="text-sm font-bold" style={{ color: COLORS.orange }}>@</span>
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={searchHint}
          className="flex-1 bg-transparent text-white text-[15px] placeholder-white/20 py-3.5 outline-none"
        />
        {searchText && (
          <button onClick={() => { setSearchText(''); setSuggestions([]); }}>
            <svg className="w-[18px] h-[18px] text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {searchText.trim() && (
        <div
          className="mt-2 rounded-2xl overflow-hidden shadow-lg max-h-[280px] overflow-y-auto absolute left-0 right-0 z-20"
          style={{ backgroundColor: COLORS.cardLight }}
        >
          {searching ? (
            <div className="flex justify-center py-5">
              <div
                className="w-[22px] h-[22px] rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: COLORS.orange, borderTopColor: 'transparent' }}
              />
            </div>
          ) : suggestions.length === 0 ? (
            <p className="text-white/30 text-[13px] p-5">
              {isPersonal ? 'No verified companies found' : 'No personal accounts found'}
            </p>
          ) : (
            suggestions.map((user, i) => (
              <button
                key={user.uid || i}
                onClick={() => proposeAffiliate(user)}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left border-b border-white/10 last:border-b-0 hover:bg-white/5"
              >
                <div
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${COLORS.orange}26` }}
                >
                  <CategoryIcon type={isPersonal ? SellerCategoryType.business : SellerCategoryType.personal} size={18} color={COLORS.orange} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">
                    {user.displayName || user.name || ''}
                  </p>
                  <p className="text-white/40 text-xs truncate">
                    @{user.username || ''}
                    {user.verification?.sellerCategory ? ` · ${user.verification.sellerCategory}` : ''}
                  </p>
                </div>
                <svg className="w-5 h-5 shrink-0" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  // ── Shared list items (used by both layouts) ───────────────────────────────

  const ProposedList = () =>
    proposed.length === 0 ? (
      <EmptyHint text="No pending proposals" />
    ) : (
      <div className="space-y-2">
        {proposed.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="#F97316" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[13.5px] font-semibold truncate">{p.targetName || ''}</p>
              <p className="text-white/40 text-[11px] truncate">@{p.targetUsername || ''} · Pending</p>
            </div>
            <button onClick={() => withdraw(p.id)}>
              <svg className="w-[18px] h-[18px] text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    );

  const AffiliatesList = () =>
    affiliates.length === 0 ? (
      <EmptyHint text={isPersonal ? 'Not affiliated with any companies yet' : 'No affiliated profiles yet'} />
    ) : (
      <div className="space-y-2">
        {affiliates.map((a) => {
          const isRequester = a.requesterUid === myUid;
          const name = isRequester ? a.targetName : a.requesterName;
          const username = isRequester ? a.targetUsername : a.requesterUsername;
          const type = isRequester ? a.targetType : a.requesterType;
          return (
            <div
              key={a.id}
              className="flex items-center gap-3 px-3.5 py-3 rounded-xl border"
              style={{ backgroundColor: COLORS.card, borderColor: `${COLORS.orange}40` }}
            >
              <div
                className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${COLORS.orange}1F` }}
              >
                <CategoryIcon type={type} size={17} color={COLORS.orange} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-[13.5px] font-semibold truncate">{name || ''}</p>
                <p className="text-white/40 text-[11px] truncate">@{username || ''}</p>
              </div>
              <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          );
        })}
      </div>
    );

  const RequestCard = ({ req }) => (
    <div className="p-3.5 rounded-2xl" style={{ backgroundColor: COLORS.card }}>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${COLORS.orange}1F` }}
        >
          <CategoryIcon type={req.requesterType} size={20} color={COLORS.orange} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">{req.requesterName || ''}</p>
          <p className="text-white/40 text-xs truncate">@{req.requesterUsername || ''}</p>
        </div>
      </div>
      <div className="flex gap-2.5 mt-3.5">
        <button
          onClick={() => respond(req.id, true)}
          className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold"
          style={{ backgroundColor: '#22C55E' }}
        >
          Approve
        </button>
        <button
          onClick={() => respond(req.id, false)}
          className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold"
          style={{ backgroundColor: '#EF4444' }}
        >
          Reject
        </button>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE HEADER (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden flex items-center gap-3 px-3 pt-2 pb-1">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
          style={{ backgroundColor: COLORS.card }}
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-white text-xl font-bold">{pageTitle}</span>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP HEADER (new) — visible at md breakpoint and up
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
        <div className="max-w-5xl mx-auto px-8 h-20 flex items-center gap-5">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
            style={{ backgroundColor: COLORS.card }}
            aria-label="Go back"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-white text-2xl font-bold leading-tight">{pageTitle}</h1>
            <p className="text-white/40 text-sm mt-0.5">{pageSubtitle}</p>
          </div>
        </div>
      </div>

      {loadingMyType ? (
        <div className="flex justify-center py-20">
          <div
            className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: COLORS.orange, borderTopColor: 'transparent' }}
          />
        </div>
      ) : (
        <>
          {/* ════════════════════════════════════════════════════════════════
              MOBILE CONTENT (unchanged) — visible below the md breakpoint
             ════════════════════════════════════════════════════════════════ */}
          <div className="md:hidden px-4 pt-2 pb-8 space-y-6">
            {/* Search bar */}
            <SearchBar />

            {/* Incoming requests */}
            {incomingRequests.length > 0 && (
              <div>
                <SectionHeader title="Requests" count={incomingRequests.length} />
                <div className="mt-2.5 space-y-2.5">
                  {incomingRequests.map((req) => (
                    <RequestCard key={req.id} req={req} />
                  ))}
                </div>
              </div>
            )}

            {/* Proposed by me */}
            <div>
              <SectionHeader title="Proposed Affiliate" count={proposed.length} />
              <div className="mt-2.5">
                <ProposedList />
              </div>
            </div>

            {/* Affiliates (approved) */}
            <div>
              <SectionHeader title={affiliatesLabel} count={affiliates.length} />
              <div className="mt-2.5">
                <AffiliatesList />
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════════
              DESKTOP CONTENT (new) — visible at md breakpoint and up.
              Search sits in its own column, incoming requests get a full-width
              card grid since they need action, and proposed/affiliates run
              side by side as a two-column dashboard.
             ════════════════════════════════════════════════════════════════ */}
          <div className="hidden md:block max-w-5xl mx-auto px-8 py-10 space-y-8">
            <div className="max-w-xl">
              <SearchBar />
            </div>

            {incomingRequests.length > 0 && (
              <div>
                <SectionHeader title="Requests" count={incomingRequests.length} />
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {incomingRequests.map((req) => (
                    <RequestCard key={req.id} req={req} />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <SectionHeader title="Proposed Affiliate" count={proposed.length} />
                <div className="mt-3">
                  <ProposedList />
                </div>
              </div>

              <div>
                <SectionHeader title={affiliatesLabel} count={affiliates.length} />
                <div className="mt-3">
                  <AffiliatesList />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {ToastEl}
    </div>
  );
};

export default AffiliationScreen;